import React from 'react';
import ReactECharts from 'echarts-for-react';

const axisNameColor = '#334155';
const labelColor = '#475569'
const axisLineColor = '#7798c3b4'
const axisLineWidth = 2;
const legendLabelColor = '#777c83'


const StackedVarianceChart = ({ label, value1, value2, chartData, chartHeight, chartHeading, xAxisTitle = '', yAxisTitle = '' }) => {
    const isEmpty = !chartData || chartData.length === 0;

    const categories = chartData ? chartData.map(item => item[label]) : [];
    const actualData = chartData.map(item => item[value2]);
    const varianceData = chartData.map(item => Math.max(0, item[value1] - item[value2]));

    const getItemColor = (actual, estimated) => {
        if (!estimated || estimated === 0) return '#10B981';
        const ratio = (actual / estimated) * 100;
        if (ratio < 50) return '#EF4444'; // Red (<50% Critical)
        if (ratio <= 75) return '#F59E0B'; // Yellow/Amber (50-75% At Risk)
        return '#10B981'; // Green (On Track / 100%)
    };

    const actualItemStyles = chartData.map(item => ({
        color: getItemColor(item[value2], item[value1]),
        borderRadius: [4, 0, 0, 4]
    }));
    // console.log(categories, "categories")
    // console.log(actualData, "actualData")
    // console.log(varianceData, "varianceData")

    const option = {
        title: {
            text: chartHeading || 'Project Efficiency: Estimated vs. Actual Time Variance (Hrs)',
            left: 'center',
            textStyle: { fontSize: 14, fontWeight: 'bold', color: '#333' }
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const idx = params[0].dataIndex;
                const item = chartData[idx];
                return `<strong>${item[label]}</strong>

                        Actual: ${item[value2]} hrs

                        Estimated: ${item[value1]} hrs`;
            }
        },
        // legend: {
        //     bottom: 0,
        //     textStyle: { fontSize: 11 }
        // },
        grid: {
            top: '15%',
            left: '5%',
            right: '8%',
            bottom: '12%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            name: xAxisTitle,
            nameLocation: "middle",
            nameGap: 35,
            nameTextStyle: {
                fontSize: 14,
                fontWeight: "bold",
                color: axisNameColor,
            },
            axisLabel: {
                show: true,
                color: labelColor,
                fontSize: 11,
                rotate: 0,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: axisLineColor,
                    width: axisLineWidth,
                },
            },
            axisTick: { show: false },
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
        },
        yAxis: {
            type: 'category',
            data: categories,
            name: yAxisTitle,
            nameLocation: "middle",
            nameGap: 150,
            nameTextStyle: {
                fontSize: 14,
                fontWeight: "bold",
                color: axisNameColor,
            },
            axisTick: { show: false },
            axisLine: {
                show: true,
                lineStyle: {
                    color: axisLineColor,
                    width: axisLineWidth,
                },
            },
            axisLabel: {
                fontSize: 11,
                fontWeight: '600',
                color: labelColor,
                rotate: 35
            }
        },
        series: [
            {
                name: 'Actual Hours',
                type: 'bar',
                stack: 'total',
                data: chartData.map((item, index) => ({
                    value: item[value2],
                    itemStyle: actualItemStyles[index]
                })),
                label: {
                    show: true,
                    position: 'insideLeft',
                    formatter: '{c} hrs Actual',
                    fontSize: 10,
                    fontWeight: 'bold',
                    color: '#fff'
                }
            },
            {
                name: 'Variance / Remaining',
                type: 'bar',
                stack: 'total',
                data: varianceData,
                itemStyle: {
                    color: '#E5E7EB',
                    borderRadius: [0, 4, 4, 0]
                },
                label: {
                    show: true,

                    position: (params) => {
                        const idx = params.dataIndex;
                        const diff =
                            chartData[idx][value1] - chartData[idx][value2];

                        return Math.abs(diff) < 5 ? 'right' : 'insideRight';
                    },

                    formatter: (params) => {
                        const idx = params.dataIndex;

                        const diff =
                            chartData[idx][value1] - chartData[idx][value2];

                        return diff !== 0
                            ? `${Math.abs(diff).toFixed(2)} hrs Variance`
                            : '';
                    },

                    fontSize: 10,
                    fontWeight: 'bold',
                    color: '#374151',

                    distance: 5
                }
            }
        ]
    };

    return (
        <div style={{ width: '100%' }}>
            {isEmpty ? (
                <div style={{ height: chartHeight || '500px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#888' }}>
                    No Data Available
                </div>
            ) : (
                <div
                    style={{
                        height: chartHeight || '400px',
                        width: '100%',
                        background: "#fff",
                        borderRadius: 12,
                        padding: 10,
                        boxShadow: "0 2px 8px rgba(0,0,0,.08)"
                    }}
                >
                    <ReactECharts
                        option={option}
                        style={{
                            height: 380,
                            width: '100%',
                        }}
                    />

                    {/* Efficiency Status Key Legend Footer */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20px',
                        marginTop: '5px',
                        fontSize: '12px',
                        fontWeight: '600',
                    }}>
                        <span>Efficiency Status Key:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '12px', height: '12px', backgroundColor: '#EF4444', display: 'inline-block', borderRadius: '2px' }}></span>
                            <span>&lt;50% (Critical)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '12px', height: '12px', backgroundColor: '#F59E0B', display: 'inline-block', borderRadius: '2px' }}></span>
                            <span>50-75% (At Risk)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '12px', height: '12px', backgroundColor: '#10B981', display: 'inline-block', borderRadius: '2px' }}></span>
                            <span>100% (On Track)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '12px', height: '12px', backgroundColor: '#d1d5d4', display: 'inline-block', borderRadius: '2px' }}></span>
                            <span>Variance / Remaining</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StackedVarianceChart;